import {
  safeLocalStorageGetItem,
  safeLocalStorageRemoveItem,
  safeLocalStorageSetItem,
} from "../utils/localStorage";

/** LocalStorage key prefix used to store last selected departments for a flyer item list. */
const STORAGE_PREFIX = "gf:lastDeptByList:";
/** Expiry window in milliseconds (30 minutes) after which a stored selection is considered stale. */
const EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Builds a localStorage key for the given flyerItemListId.
 * @param flyerItemListId - Unique identifier of the flyer item list.
 * @returns Storage key string.
 */
const getKey = (flyerItemListId: string) =>
  `${STORAGE_PREFIX}${flyerItemListId}`;

/**
 * Acceptable shapes for an identifier input. Can be a string/number or an object with an `id` property.
 */
export type IdLike = string | number;

/** Representation of the value persisted in localStorage. */
type StoredValue = {
  ids: string[];
  lastUpdatedTimestamp: number; // epoch millis
};

/**
 * Checks whether a timestamp has expired according to the configured expiry window.
 * @param ts - Epoch milliseconds.
 * @returns true if expired; false otherwise.
 */
function isExpired(ts: number) {
  return Date.now() - ts > EXPIRY_MS;
}

/**
 * Reads the last selected department IDs for a given flyerItemListId from localStorage.
 *
 * Behavior:
 *  - Migrates legacy array-only entries into the structured format with a fresh timestamp.
 *  - If the stored entry has expired (> 30 minutes), the entry is removed and null is returned.
 *
 * @param flyerItemListId - The flyer list id used to construct the storage key.
 * @returns An array of department IDs as strings, or null if none/expired.
 */
export function readLastSelectedDepartmentIds(
  flyerItemListId?: string
): string[] | null {
  if (!flyerItemListId) return null;
  try {
    const key = getKey(flyerItemListId);
    const raw = safeLocalStorageGetItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const ids = parsed.map(String);
      const toStore: StoredValue = {
        ids,
        lastUpdatedTimestamp: Date.now(),
      };
      safeLocalStorageSetItem(key, JSON.stringify(toStore));
      return ids;
    }
    // New format
    if (parsed && Array.isArray(parsed.ids)) {
      const sv = parsed as StoredValue;
      if (typeof sv.lastUpdatedTimestamp === "number") {
        if (isExpired(sv.lastUpdatedTimestamp)) {
          // Clear if expired
          safeLocalStorageRemoveItem(key);
          return null;
        }
      }
      return sv.ids.map(String);
    }
    return null;
  } catch {
    return null;
  }
}

// Persist last selected department IDs
/**
 * Persists the last selected department IDs for the given flyerItemListId and
 * updates the lastUpdatedTimestamp to current time.
 *
 * @param flyerItemListId - The flyer list id used to construct the storage key.
 * @param ids - The IDs to store. Can be `IdLike` items or objects with an `id` field.
 */
export function writeLastSelectedDepartmentIds(
  flyerItemListId: string,
  ids: IdLike[]
) {
  try {
    const asStrings = (ids || []).map((v) => String((v as any)?.id ?? v));
    const value: StoredValue = {
      ids: asStrings,
      lastUpdatedTimestamp: Date.now(),
    };
    safeLocalStorageSetItem(getKey(flyerItemListId), JSON.stringify(value));
  } catch {
    // no-op
  }
}

/**
 * Removes the persisted entry for the given flyerItemListId.
 * @param flyerItemListId - The flyer list id used to construct the storage key.
 */
export function clearLastSelectedDepartmentIds(flyerItemListId?: string) {
  if (!flyerItemListId) return;
  try {
    safeLocalStorageRemoveItem(getKey(flyerItemListId));
  } catch {
    // no-op
  }
}

// React hook sugar for components that want a simple API
import { useCallback, useEffect, useState } from "react";

/**
 * React hook for working with the last selected departments per flyer item list.
 *
 * By "last selected", we mean the department ID(s) most recently used to
 * create or update a flyer item in the specified flyer item list.
 *
 * Responsibilities:
 *  - Reads the selection from localStorage for the given flyerItemListId.
 *  - Automatically clears and returns null if the stored value is older than 30 minutes.
 *  - Provides recordSelection to persist a new selection (refreshing the timestamp),
 *    clearSelection to remove the stored entry, and readSelection to fetch current storage value.
 *
 * @param flyerItemListId - The active flyer item list id context.
 * @returns An object containing the currently read ids, and helpers to record/clear/read selections.
 */
export function useLastSelectedDepartment(flyerItemListId?: string) {
  const [lastIds, setLastIds] = useState<string[] | null>(null);

  useEffect(() => {
    setLastIds(readLastSelectedDepartmentIds(flyerItemListId));
  }, [flyerItemListId]);

  /**
   * Persists the provided selection to localStorage for the current flyerItemListId.
   * If `ids` is falsy, nothing is written.
   * @param ids - Array of IDs or objects with an `id` field.
   */
  const recordSelection = useCallback(
    (ids: IdLike[] | undefined | null) => {
      if (!flyerItemListId || !ids) return;
      writeLastSelectedDepartmentIds(flyerItemListId, ids);
      setLastIds((ids || []).map((v) => String((v as any)?.id ?? v)));
    },
    [flyerItemListId]
  );

  /**
   * Clears the stored selection for the current flyerItemListId and resets the local state.
   */
  const clearSelection = useCallback(() => {
    if (!flyerItemListId) return;
    clearLastSelectedDepartmentIds(flyerItemListId);
    setLastIds(null);
  }, [flyerItemListId]);

  return {
    lastDepartmentIds: lastIds,
    recordSelection,
    clearSelection,
    readSelection: () => readLastSelectedDepartmentIds(flyerItemListId),
  };
}
